// RunLinesScreen — lifted from the MagicPatterns mockup (handoff 2026-06-25),
// wired to her actual take + skip-mine playback.
//
// Visual structure unchanged. Data sources changed:
//   - SCENE is built from arc.parsedScript + arc.selectedCharacter
//   - Audio plays from the take fetched via getTake() (audio_url + manifest)
//   - "My lines" toggle drives skip-mine mode (auto-seeks past actor segments)
//   - Cast pill is rendered DISABLED for v0 — ElevenLabs voicing is Phase 2
//   - Stage Manager toggle is UI-only for v0
//   - Media Session API metadata wired so lock-screen / car BT controls work
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XIcon,
  MicIcon,
  PlayIcon,
  PauseIcon,
  SkipBackIcon,
  SkipForwardIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useArc } from '../state/arc';
import { getTake, Take, TakeManifestEntry } from '../api/client';

type Line = {
  speaker: 'YOU' | 'OTHER';
  character: string;
  text: string;
  lineId: number;
};

// Placeholder voice names for the Cast row. Real ElevenLabs assignment lands
// in Phase 2; for v0 the pill renders but is not interactive.
const VOICE_PLACEHOLDER = 'Stage Reader';

export function RunLinesScreen() {
  const navigate = useNavigate();
  const { state } = useArc();
  const script = state.parsedScript;
  const projectId = state.projectId;
  const takeId = state.takeId;
  const character = state.selectedCharacter;

  const [take, setTake] = useState<Take | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [myLinesOn, setMyLinesOn] = useState(true);
  const [currentLineId, setCurrentLineId] = useState<number | null>(null);

  // Build the scene from real script data
  const SCENE: Line[] = useMemo(() => {
    if (!script) return [];
    return script.lines
      .filter((l) => l.character && l.text?.trim())
      .map((l) => ({
        speaker: (l.character || '').toUpperCase() === (character || '').toUpperCase() ? 'YOU' : 'OTHER',
        character: l.character || '',
        text: l.text,
        lineId: l.id,
      }));
  }, [script, character]);

  // Find current line index by lineId so the visual sync works
  const idx = useMemo(() => {
    if (currentLineId == null) return 0;
    const found = SCENE.findIndex((l) => l.lineId === currentLineId);
    return found >= 0 ? found : 0;
  }, [SCENE, currentLineId]);
  const line = SCENE[idx];
  const isYou = line?.speaker === 'YOU';
  const liveGap = playing && isYou && !myLinesOn;

  // --- Take fetch -------------------------------------------------------
  useEffect(() => {
    if (!projectId || !takeId) {
      setLoadError(
        'No recording yet for this script. Tap the booth icon to record one.'
      );
      return;
    }
    let cancelled = false;
    getTake(projectId, takeId)
      .then((t) => { if (!cancelled) setTake(t); })
      .catch((e) => { if (!cancelled) setLoadError(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, [projectId, takeId]);

  // --- Audio plumbing + skip-mine seek ---------------------------------
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sortedManifest = useMemo(
    () => (take?.manifest ?? []).slice().sort((a, b) => a.start_ms - b.start_ms),
    [take]
  );
  const myLinesOnRef = useRef(myLinesOn);
  myLinesOnRef.current = myLinesOn;

  const findSegmentAtMs = (ms: number): TakeManifestEntry | null => {
    for (const seg of sortedManifest) {
      if (ms >= seg.start_ms && ms < seg.end_ms) return seg;
    }
    return null;
  };

  const isActorLineId = (lineId: number): boolean => {
    const sl = SCENE.find((s) => s.lineId === lineId);
    return sl?.speaker === 'YOU';
  };

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !take) return;

    const onTimeUpdate = () => {
      const ms = el.currentTime * 1000;
      const seg = findSegmentAtMs(ms);
      setCurrentLineId(seg ? seg.line_id : null);
      if (!seg) return;
      if (!myLinesOnRef.current && isActorLineId(seg.line_id)) {
        // Jump to just past this actor segment.
        el.currentTime = (seg.end_ms + 10) / 1000;
      }
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);

    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    return () => {
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
    };
  }, [take, sortedManifest, SCENE]);

  // --- Media Session API (lock screen + car BT) ------------------------
  useEffect(() => {
    if (!('mediaSession' in navigator) || !script) return;
    const ms: any = navigator.mediaSession;
    try {
      ms.metadata = new (window as any).MediaMetadata({
        title: script.title,
        artist: character || '',
        album: 'Run Lines',
        artwork: [],
      });
    } catch { /* no-op */ }
    const safe = (fn: () => void) => () => { try { fn(); } catch { /* no-op */ } };
    const el = audioRef.current;
    try {
      ms.setActionHandler('play', safe(() => el?.play()));
      ms.setActionHandler('pause', safe(() => el?.pause()));
      ms.setActionHandler('seekbackward', safe(() => { if (el) el.currentTime = Math.max(0, el.currentTime - 10); }));
      ms.setActionHandler('seekforward', safe(() => { if (el) el.currentTime = el.currentTime + 10; }));
    } catch { /* no-op */ }
    return () => {
      try {
        ms.setActionHandler('play', null);
        ms.setActionHandler('pause', null);
        ms.setActionHandler('seekbackward', null);
        ms.setActionHandler('seekforward', null);
      } catch { /* no-op */ }
    };
  }, [script, character, take]);

  // --- Transport controls -----------------------------------------------
  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play(); else el.pause();
  };
  const step = (dir: number) => {
    const el = audioRef.current;
    if (!el || !SCENE.length) return;
    const target = Math.max(0, Math.min(SCENE.length - 1, idx + dir));
    const seg = sortedManifest.find((s) => s.line_id === SCENE[target].lineId);
    if (seg) el.currentTime = seg.start_ms / 1000;
  };

  // --- Status chip text ------------------------------------------------
  const status = (() => {
    if (loadError) return 'No take loaded';
    if (!playing) return 'Paused';
    if (liveGap) return 'Your line — live';
    if (isYou) return 'Playing your take';
    return `${VOICE_PLACEHOLDER} · voicing ${line?.character ?? ''}`;
  })();

  // --- Guard: no script
  if (!script || !character) {
    return (
      <div className="h-full w-full bg-cream-light flex flex-col items-center justify-center text-gray-600 p-8 text-center gap-6">
        <p className="font-serif italic">No script loaded.</p>
        <button
          onClick={() => navigate('/library')}
          className="font-mono text-[10px] uppercase tracking-[0.28em] text-gray-700 border border-gray-900/15 rounded-full px-5 py-3 active:bg-gray-900/5"
        >
          ← Back to Library
        </button>
      </div>
    );
  }

  // --- Guard: no recording yet — give her a clear CTA instead of a
  // disabled player. Per Ryan iPhone test 2026-06-25.
  if (!takeId) {
    return (
      <div className="h-full w-full bg-cream-light flex flex-col items-center justify-center text-center px-8 gap-6">
        <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-gray-400">
          {script.title}
        </p>
        <p className="font-serif italic text-gray-600 text-xl max-w-[280px] leading-snug">
          You haven't recorded a take yet. Step into the booth to read the scene through.
        </p>
        <button
          onClick={() => navigate('/record')}
          className="font-mono text-[10px] uppercase tracking-[0.28em] text-white bg-rose-600 active:bg-rose-500 rounded-full px-6 py-4 mt-2"
        >
          Enter the Booth
        </button>
        <button
          onClick={() => navigate('/room')}
          className="font-mono text-[9px] uppercase tracking-[0.25em] text-gray-500 active:text-gray-800"
        >
          ← Rehearsal Room
        </button>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-cream-light relative overflow-hidden flex flex-col text-gray-900">
      {/* Subtle Aurora Edge Lighting */}
      <div className="absolute top-0 left-0 right-0 h-[150px] bg-gradient-to-b from-rose-900/5 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-[200px] bg-gradient-to-t from-rose-900/5 to-transparent pointer-events-none" />

      {/* Large ambient Presence — the listener — anchored behind the scene */}
      <div
        className="presence-ambient"
        style={{
          top: '12%',
          left: '50%',
          marginLeft: -210,
          opacity: liveGap ? 0.32 : 0.2,
        }}
      />

      {/* Header — close only. Stage Manager will land in a future phase
          (Ryan 2026-06-25: don't ship toggles for features that don't exist). */}
      <div className="w-full pt-14 px-6 flex items-center justify-end relative z-20 shrink-0">
        <button
          onClick={() => navigate('/room')}
          aria-label="Leave Run Lines"
          className="w-9 h-9 rounded-full border border-gray-900/12 flex items-center justify-center text-gray-700 active:bg-gray-900/5 transition-colors"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Title */}
      <div className="px-7 pt-3 relative z-10 shrink-0">
        <h1 className="font-display text-3xl tracking-wide text-gray-900 leading-none">
          Run Lines
        </h1>
        <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-gray-400 mt-2">
          {script.title}
        </p>
      </div>

      {/* The Presence (listener) + the live line */}
      <div className="flex-1 w-full relative z-10 flex flex-col items-center justify-center px-8">
        <motion.div
          className="relative w-[210px] h-[210px] flex items-center justify-center mb-6"
          animate={{ scale: playing ? 1.03 : 0.96, y: [0, -8, 0] }}
          transition={{
            scale: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
            y: { duration: 6, ease: 'easeInOut', repeat: Infinity },
          }}
        >
          {/* Leather-cover CSS fallback (per handoff: preserve both paths). */}
          <div className="leather-cover leather-navy" style={{ width: 124, height: 196 }} />

          {/* Listening pulse while live gap */}
          <AnimatePresence>
            {liveGap && (
              <motion.span
                className="absolute rounded-full border border-gray-900/20"
                style={{ width: 196, height: 196 }}
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: [0.9, 1.18, 0.9], opacity: [0.5, 0, 0.5] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              />
            )}
          </AnimatePresence>
        </motion.div>

        {/* Status chip */}
        <span className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.26em] mb-3 text-gray-400">
          {liveGap && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
          {status}
        </span>

        {/* Current line — masked when it's your live gap */}
        <div className="min-h-[64px] flex items-center text-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={(line?.lineId ?? 'na') + status}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3 }}
              className={`font-serif italic text-xl leading-relaxed max-w-[300px] ${liveGap ? 'text-gray-300' : 'text-gray-700'}`}
            >
              {liveGap ? '· · · · ·' : line?.text ? `“${line.text}”` : ' '}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>

      {/* Cast row — Phase 2 will wire ElevenLabs voices. For v0 the pill
          renders disabled-but-visible to keep the visual structure intact. */}
      <div className="px-7 pt-4 relative z-10 shrink-0 flex items-center gap-2.5">
        <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-gray-400 mr-1">
          Cast
        </span>
        <span className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-gray-900/12 opacity-60">
          <span className="presence-mark" style={{ width: 18, height: 18 }} />
          <span className="text-[11px] text-gray-700">
            Partners · <span className="text-gray-500">your voice</span>
          </span>
        </span>
        <span className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-gray-900/12">
          <span className="w-[18px] h-[18px] rounded-full bg-gray-900 flex items-center justify-center">
            <MicIcon className="w-2.5 h-2.5 text-white" />
          </span>
          <span className="text-[11px] text-gray-700">
            {character} · <span className="text-gray-500">You</span>
          </span>
        </span>
      </div>

      {/* Mode + transport */}
      <div className="px-7 pt-4 pb-9 relative z-10 shrink-0">
        {/* My lines toggle */}
        <div className="flex items-center justify-center mb-5">
          <button
            onClick={() => setMyLinesOn((v) => !v)}
            className="flex items-center gap-2"
            aria-pressed={myLinesOn}
          >
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-gray-500">
              My lines
            </span>
            <span className={`w-8 h-4 rounded-full relative transition-colors ${myLinesOn ? 'bg-gray-900' : 'bg-gray-300'}`}>
              <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${myLinesOn ? 'left-4' : 'left-0.5'}`} />
            </span>
          </button>
        </div>

        {/* Transport */}
        <div className="flex items-center justify-center gap-8">
          <button
            onClick={() => step(-1)}
            aria-label="Previous line"
            className="w-11 h-11 rounded-full flex items-center justify-center text-gray-600 active:bg-gray-900/5 transition-colors"
          >
            <SkipBackIcon className="w-5 h-5" />
          </button>

          <button
            onClick={togglePlay}
            disabled={!take}
            aria-label={playing ? 'Pause' : 'Play'}
            className={`w-16 h-16 rounded-full bg-gray-900 flex items-center justify-center shadow-lg active:scale-95 transition-transform ${!take ? 'opacity-50' : ''}`}
          >
            {playing ? (
              <PauseIcon className="w-7 h-7 text-white fill-white" />
            ) : (
              <PlayIcon className="w-7 h-7 text-white fill-white ml-0.5" />
            )}
          </button>

          <button
            onClick={() => step(1)}
            aria-label="Next line"
            className="w-11 h-11 rounded-full flex items-center justify-center text-gray-600 active:bg-gray-900/5 transition-colors"
          >
            <SkipForwardIcon className="w-5 h-5" />
          </button>
        </div>

        {loadError && (
          <p className="mt-4 text-center font-mono text-[10px] text-rose-700/80">
            {loadError}
          </p>
        )}
      </div>

      {/* The single <audio> element — loaded as soon as the take is fetched.
          preload="auto" lets iOS Safari buffer enough for lock-screen play. */}
      {take?.audio_url && (
        <audio
          ref={audioRef}
          src={take.audio_url}
          preload="auto"
          playsInline
        />
      )}
    </div>
  );
}
